import { memo, ReactNode, useCallback, useMemo, useState } from 'react';
import { FormField } from '@amzn/awsui-components-core';
import { TiptapOutputType, convertStringToTiptapOutput } from '@amzn/awscat-editor';
import styles from './CompactTiptapEditorStyles.module.scss';
import { isWithinRange } from './InlineValidations';
import { useEntities } from '../v2-hooks/useEntities';
import { useEntityConverter } from '../v2-hooks/useEntityConverter';
import { TiptapEditorEditingMode } from './tiptap-editor-integration';
import { InlineTextFilter } from '../../services/InlineTextOperations';

// Stable references to prevent unnecessary re-renders of TiptapEditor.
const EMPTY_MENU_ITEMS: never[] = [];
const OUTPUT_FORMATS: ('json' | 'text')[] = ['json', 'text'];

interface TitleInputProps {
  /** The initial title value. Read once on mount; updates are emitted via onTitleChanged. */
  readonly initialTitle: TiptapOutputType;
  /** Inclusive minimum number of characters allowed. */
  readonly minLength: number;
  /** Inclusive maximum number of characters allowed. */
  readonly maxLength: number;
  /** Label rendered above the input. */
  readonly label?: ReactNode;
  /** Helper text rendered below the label. */
  readonly description?: ReactNode;
  /** Whether to auto-focus the editor on mount. Defaults to false. */
  readonly autoFocus?: boolean;
  /** If this input displays inline edit, specify what to display here */
  readonly inlineTextFilter?: InlineTextFilter;
  /** Called when Reply reveals an inline comment in the sidebar. */
  readonly onCommentReply?: () => void;
  /**
   * Called whenever the title changes. `isValidationPassed` is true when the
   * title's length is within [minLength, maxLength]; callers should disable
   * Save when it is false.
   */
  readonly onTitleChanged: (title: TiptapOutputType, isValidationPassed: boolean) => void;
}

/**
 * Tiptap-based title input with built-in length validation. Shows an inline
 * error when the title is non-empty and out of bounds (an empty input stays
 * silent so the user isn't yelled at before they've typed anything).
 */
const TitleInput = memo(function TitleInput({
  initialTitle,
  minLength,
  maxLength,
  label,
  description,
  autoFocus = false,
  onTitleChanged,
  inlineTextFilter,
  onCommentReply,
}: TitleInputProps) {
  // Title can contain replacement entities. Load them
  const entities = useEntities();
  const entityConverter = useEntityConverter();

  // Snapshot the initial value once; subsequent prop changes don't rebuild the editor.
  const initialContent = useMemo(
    () =>
      typeof initialTitle === 'string' ? convertStringToTiptapOutput(initialTitle) : initialTitle,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  // Track the latest title so we can decide whether to show the inline error.
  const [latestTitle, setLatestTitle] = useState<TiptapOutputType>(initialTitle);

  // Suppress the error for an empty input — the user hasn't typed anything yet.
  const latestTitleStr = entityConverter(latestTitle);
  const showError =
    !isWithinRange(latestTitleStr, 0, 0) && !isWithinRange(latestTitleStr, minLength, maxLength);

  // When the editor content changes, update local state and notify the caller.
  const handleChange = useCallback(
    (_editor: unknown, output: Record<string, any>) => {
      setLatestTitle(output.json);
      const outputStr = entityConverter(output.json);
      onTitleChanged(output.json, isWithinRange(outputStr, minLength, maxLength));
    },
    [entityConverter, onTitleChanged, minLength, maxLength],
  );

  return (
    <FormField
      label={label}
      description={description}
      errorText={
        showError
          ? `Text length must be between ${minLength} and ${maxLength} characters.`
          : undefined
      }
      stretch
    >
      <div className={`${styles.container} ${showError ? styles.containerError : ''}`}>
        <TiptapEditorEditingMode
          content={initialContent}
          editable
          removeFormatting
          hideMenuBar
          hideFooter
          menuItems={EMPTY_MENU_ITEMS}
          entities={entities}
          outputFormats={OUTPUT_FORMATS}
          onChange={handleChange}
          autoFocus={autoFocus}
          inlineTextFilter={inlineTextFilter}
          onCommentReply={onCommentReply}
        />
      </div>
    </FormField>
  );
});

export default TitleInput;
